import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Bed, Bath, Users, MapPin, ArrowRight, 
  Waves, Film, Flame, Wifi, ChefHat, Dumbbell
} from 'lucide-react';
import axios from 'axios';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL - Updated Dec 22, 2025
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
};

const amenityIcons = {
  waves: Waves,
  film: Film,
  flame: Flame,
  wifi: Wifi,
  'chef-hat': ChefHat,
  dumbbell: Dumbbell,
};

export default function HomePage() {
  const [property, setProperty] = useState(null);
  const [gallery, setGallery] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [homepageContent, setHomepageContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Seed data first
        await axios.post(`${API}/seed`);
        
        const [propRes, galleryRes, amenitiesRes, homepageRes] = await Promise.all([
          axios.get(`${API}/property`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/amenities?category=features`),
          axios.get(`${API}/homepage-content`),
        ]);
        setProperty(propRes.data);
        setGallery(galleryRes.data);
        setAmenities(amenitiesRes.data.slice(0, 6));
        setHomepageContent(homepageRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Get images - prefer homepage content, fallback to gallery
  const heroImage = !loading && (
    homepageContent?.hero_image || 
    property?.hero_image || 
    gallery.find(img => img.category === 'exterior')?.url
  ) || null;

  // About section images (5 images)
  const aboutImages = homepageContent?.about_images?.length > 0 
    ? homepageContent.about_images.map((url, idx) => ({ id: `about-${idx}`, url, alt: `About image ${idx + 1}` }))
    : gallery.slice(0, 5);

  // Gallery preview images (6 images)
  const galleryPreviewImages = homepageContent?.gallery_preview_images?.length > 0
    ? homepageContent.gallery_preview_images.map((url, idx) => ({ id: `preview-${idx}`, url, alt: `Gallery preview ${idx + 1}` }))
    : gallery.slice(0, 6);

  // CTA background image
  const ctaBackgroundImage = homepageContent?.cta_background_image || gallery[1]?.url || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920';

  // Process hero image through getImageUrl
  const heroImageUrl = heroImage ? getImageUrl(heroImage) : null;
  const ctaImageUrl = getImageUrl(ctaBackgroundImage);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden">
        <Navbar transparent />
        
        {/* Background Image - only show after loading */}
        <div 
          className={`absolute inset-0 bg-cover bg-center transition-opacity duration-500 ${heroImageUrl ? 'opacity-100' : 'opacity-0'}`}
          style={{ 
            backgroundImage: heroImageUrl ? `url(${heroImageUrl})` : 'none',
            backgroundColor: '#1B4D3E'
          }}
        >
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex items-center justify-center text-center text-white container-padding">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-4xl"
          >
            <p className="text-sm tracking-widest uppercase mb-6 text-[#D4AF37]">
              Kawartha Luxury Properties
            </p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl font-medium mb-6">
              Big Bear Estate
            </h1>
            <p className="text-xl md:text-2xl text-white/80 mb-8 font-light">
              {property?.tagline || 'A Retreat From The Ordinary'}
            </p>
            
            {/* Property Stats */}
            <div className="flex flex-wrap justify-center gap-8 mb-10">
              <div className="flex items-center gap-2">
                <Bed className="w-5 h-5 text-[#D4AF37]" />
                <span>{property?.bedrooms || 7} Bedrooms</span>
              </div>
              <div className="flex items-center gap-2">
                <Bath className="w-5 h-5 text-[#D4AF37]" />
                <span>{property?.bathrooms || 5} Bathrooms</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#D4AF37]" />
                <span>Sleeps {property?.sleeps || 15}</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                className="btn-primary inline-flex items-center justify-center gap-2"
                data-testid="hero-book-now"
              >
                Check Availability
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/gallery"
                className="inline-flex items-center justify-center px-8 py-3 text-sm font-medium tracking-wider uppercase bg-white/90 text-[#1B4D3E] border-2 border-white rounded-md hover:bg-white transition-colors duration-300"
                data-testid="hero-view-gallery"
              >
                View Gallery
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-white/70 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Location Bar */}
      <section className="bg-[#1B4D3E] text-white py-6">
        <div className="container-padding flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <MapPin className="w-5 h-5 text-[#D4AF37]" />
            <span>{property?.location || 'Jack Lake, Apsley, Ontario, Canada'}</span>
          </div>
          <p className="text-white/70 text-sm">
            5,500+ sq ft • 1.42 Acres • 162 ft Shoreline
          </p>
        </div>
      </section>

      {/* About Section */}
      <section className="section-padding">
        <div className="container-padding">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-caption text-[#D4AF37] mb-4">Welcome to</p>
              <h2 className="heading-section text-[#1C1C1C] mb-6">
                A Sanctuary on Jack Lake
              </h2>
              <p className="text-body-lg text-[#66605B] mb-6">
                {property?.description || 
                  'The epitome of lake living under the roof of one luxurious, exquisite family residence. Located on the West shore of Jack Lake in the Kawarthas, this luxury home offers over 5,500 square feet of glamorous yet relaxed lakefront living.'}
              </p>
              <p className="text-[#66605B] mb-8">
                In the main home, the great room, dining room and kitchen have 20 foot high beamed 
                wood tongue-and-groove ceilings with stunning views of the Lake, the nature preserve 
                and adjacent forest.
              </p>
              <Link
                to="/amenities"
                className="inline-flex items-center gap-2 text-[#1B4D3E] font-medium hover:text-[#D4AF37] transition-colors"
              >
                Discover All Amenities
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid grid-cols-2 gap-4"
            >
              {aboutImages.slice(0, 5).map((img, idx) => (
                <div 
                  key={img.id}
                  className={`image-hover rounded-sm overflow-hidden ${
                    idx === 0 ? 'col-span-2 aspect-video' : 'aspect-square'
                  }`}
                >
                  <img 
                    src={getImageUrl(img.url)} 
                    alt={img.alt}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Amenities Preview */}
      <section className="section-padding bg-[#F0EBE5]">
        <div className="container-padding">
          <div className="text-center mb-16">
            <p className="text-caption text-[#D4AF37] mb-4">Features</p>
            <h2 className="heading-section text-[#1C1C1C]">
              World-Class Amenities
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {amenities.map((amenity, idx) => {
              const IconComponent = amenityIcons[amenity.icon] || Waves;
              return (
                <motion.div
                  key={amenity.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  className="bg-white p-6 md:p-8 rounded-sm shadow-sm hover:shadow-md transition-shadow"
                >
                  <IconComponent className="w-8 h-8 text-[#1B4D3E] mb-4" />
                  <h3 className="font-serif text-lg text-[#1C1C1C]">
                    {amenity.name}
                  </h3>
                </motion.div>
              );
            })}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/amenities"
              className="btn-secondary inline-flex items-center gap-2"
              data-testid="view-all-amenities"
            >
              View All Amenities
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="section-padding">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
            <div>
              <p className="text-caption text-[#D4AF37] mb-4">Gallery</p>
              <h2 className="heading-section text-[#1C1C1C]">
                Explore the Estate
              </h2>
            </div>
            <Link
              to="/gallery"
              className="inline-flex items-center gap-2 text-[#1B4D3E] font-medium hover:text-[#D4AF37] transition-colors"
            >
              View Full Gallery
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 auto-rows-[200px] md:auto-rows-[250px]">
            {galleryPreviewImages.slice(0, 6).map((img, idx) => {
              const gridClass = idx === 0 
                ? 'md:col-span-8 md:row-span-2' 
                : 'md:col-span-4';
              
              return (
                <motion.div
                  key={img.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  className={`image-hover rounded-sm overflow-hidden ${gridClass}`}
                >
                  <img 
                    src={getImageUrl(img.url)} 
                    alt={img.alt}
                    className="w-full h-full object-cover"
                  />
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ 
            backgroundImage: `url(${ctaImageUrl})` 
          }}
        >
          <div className="absolute inset-0 bg-[#1B4D3E]/80" />
        </div>

        <div className="relative z-10 container-padding text-center text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-caption text-[#D4AF37] mb-4">Ready to Experience?</p>
            <h2 className="font-serif text-4xl md:text-6xl mb-6">
              Book Your Retreat
            </h2>
            <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">
              Weekly rentals available from May through November. 
              Contact us to check availability and reserve your dates.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/booking"
                className="bg-[#D4AF37] text-[#1C1C1C] px-8 py-4 rounded-sm uppercase tracking-widest text-xs font-bold hover:bg-[#c9a432] transition-colors"
                data-testid="cta-book-now"
              >
                Check Availability
              </Link>
              <a
                href="tel:416-543-4749"
                className="border border-white text-white px-8 py-4 rounded-sm uppercase tracking-widest text-xs font-bold hover:bg-white hover:text-[#1B4D3E] transition-colors"
              >
                Call 416.543.4749
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
